import React, { useState, useEffect } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../ui/dialog";
import { Button } from "../ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../ui/select";
import { Copy, Loader2, ArrowRight } from "lucide-react";
import { API } from "../../App";
import { toast } from "sonner";

/**
 * Clone all inventory items from one store location to another.
 * Uses the SKU-upsert semantics on the backend — existing SKUs at the target
 * are updated in place, new SKUs are created. Photos are intentionally not
 * copied (photos are item-scoped, not location-scoped).
 *
 * Props:
 *   open, onOpenChange
 *   storeLocations — [{location_id, name, code?}]
 *   currentLocationId — pre-fills target when the user has a location selected
 *   onCloned(result)  — callback with {copied, created, updated}
 */
export default function CloneLocationModal({
  open,
  onOpenChange,
  storeLocations = [],
  currentLocationId = "",
  onCloned,
}) {
  const [sourceId, setSourceId] = useState("");
  const [targetId, setTargetId] = useState(currentLocationId || "");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (open) {
      setTargetId(currentLocationId || "");
      setSourceId("");
    }
  }, [open, currentLocationId]);

  const close = () => {
    onOpenChange(false);
    setTimeout(() => { setSourceId(""); setLoading(false); }, 250);
  };

  const run = async () => {
    if (!sourceId || !targetId) {
      toast.error("Pick a source and a target location");
      return;
    }
    if (sourceId === targetId) {
      toast.error("Source and target must be different");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/inventory/clone-location`,
        { source_location_id: sourceId, target_location_id: targetId },
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } },
      );
      toast.success(
        data.copied
          ? `Cloned ${data.copied} item${data.copied === 1 ? "" : "s"} (${data.created} new, ${data.updated} updated)`
          : (data.message || "Nothing to copy"),
      );
      if (typeof onCloned === "function") onCloned(data);
      close();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clone failed");
    } finally {
      setLoading(false);
    }
  };

  const locOption = (loc) => (
    <SelectItem key={loc.location_id} value={loc.location_id}>
      {loc.name}{loc.code ? ` (${loc.code})` : ""}
    </SelectItem>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) close(); else onOpenChange(true); }}>
      <DialogContent className="max-w-md" data-testid="clone-location-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Copy className="w-4 h-4" /> Clone Inventory to Another Location
          </DialogTitle>
          <DialogDescription>
            Copies every item from the source to the target. Existing SKUs at the target are updated — new ones are created. Photos are not copied.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-2">
          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">From</label>
            <Select value={sourceId || "_none_"} onValueChange={(v) => setSourceId(v === "_none_" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="clone-source-select">
                <SelectValue placeholder="Choose source location…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">— Choose source —</SelectItem>
                {storeLocations.map(locOption)}
              </SelectContent>
            </Select>
          </div>

          <div className="flex justify-center text-muted-foreground">
            <ArrowRight className="w-4 h-4" />
          </div>

          <div>
            <label className="text-xs font-medium text-muted-foreground block mb-1">To</label>
            <Select value={targetId || "_none_"} onValueChange={(v) => setTargetId(v === "_none_" ? "" : v)}>
              <SelectTrigger className="h-9" data-testid="clone-target-select">
                <SelectValue placeholder="Choose target location…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="_none_">— Choose target —</SelectItem>
                {storeLocations.filter((l) => l.location_id !== sourceId).map(locOption)}
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={close} disabled={loading}>Cancel</Button>
          <Button
            className="btn-primary"
            onClick={run}
            disabled={loading || !sourceId || !targetId || sourceId === targetId}
            data-testid="clone-execute-btn"
          >
            {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Copy className="w-4 h-4 mr-2" />}
            Clone items
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
